/**
 * THE BIG OAK — a life-sized, open-grown oak with a real skeleton inside it: a short stout leaning
 * bole, a crotch of heavy limbs, and one forking rule repeated until every branch has reached its own
 * patch of the crown's surface.
 *
 * See treeOak.ts for the shape of a spec file and for the LOD ladder. What is different here is the
 * MODEL: the small oak is a trunk with a blob balanced on it, which is the right picture of a 6 m
 * sapling seen among a hundred others. This one is a landmark you walk under, so it is built the way
 * the bare tree in the reference photograph is built, and it is built by ONE rule:
 *
 *  1. THE CROWN IS A SPHERE SECTOR CENTRED ON THE CROTCH. Radius `R` (world config) from the crotch;
 *     the surface is every direction within `range / 2` of vertical — range 0 is a spike straight up,
 *     180 a hemisphere sitting on the crotch whose lowest limbs leave horizontally, 240 a crown whose
 *     lowest limbs leave 30° below horizontal and hang under the crotch. That surface is the whole
 *     shape: no dome exponent, no widest line, no dished base.
 *  2. BRANCH ENDS ARE TILED EVENLY OVER THAT SURFACE at `density` per m² (a Fibonacci lattice on the
 *     cap, each tip JITTERED along the surface by up to half the lattice spacing so it does not read
 *     as a texture). Tips are therefore where the leaves go, uniformly, by construction — and there
 *     is no underside to be bald, because the cap is the whole surface.
 *  3. THE BOLE is short, stout and LEANING: flared at the foot, tilted a few degrees off vertical
 *     and curving a little further as it rises, constant radius up to the crotch. It does not narrow
 *     before it divides.
 *  4. THE RECURSION. A branch owns a SET of tips. It heads straight for the set's centroid, goes
 *     LEN_FRAC of the way, and forks: the set is split in two about its FARTHEST PAIR (each other tip
 *     joins the nearer) and each half grows on from the fork. A set of one tip runs straight to it
 *     and ends. Every path therefore ends ON the surface; nothing is a stub and nothing is clamped.
 *     Segments are straight — the forks supply all the shape a bare oak has. A fork whose children
 *     would diverge by more than MAX_FORK_ANGLE is pulled back toward its parent until they do not:
 *     seen from further off, two targets subtend a smaller angle.
 *     AT THE CROTCH the cap of the crown (within LEADER_ANGLE of vertical) is one limb's set — the
 *     LEADER, which therefore rises steeply — and the rest is cut into spreaders by farthest pair.
 *     PRUNING (world config, a percentage): the first time a path's set falls to GAP_TIPS or fewer,
 *     that sub-branch is dropped outright with that probability. Tested ONCE per path, so the
 *     percentage set is the share of the crown actually lost, whatever the recursion's depth —
 *     testing at every layer compounded (29% gone at a nominal 12%). The territory is left as a gap
 *     the exact shape of a lost limb, its neighbours never grow into it (they never owned those
 *     tips), and its parent keeps the radius its full count gave it — which is what a limb that lost
 *     a child looks like.
 *  5. RADII BY LEONARDO'S RULE, BY COUNT: a branch serving n of the N tips has radius
 *     TRUNK_R × (n / N)^(1/2), so the children's cross-sections at every fork sum to the parent's.
 *     The crotch limbs LEAVE AT THE BOLE'S OWN RADIUS and taper to theirs over their first segment —
 *     the swollen crotch of a real oak. Floored at the lattice floor, which the last few orders sit
 *     on — those are the twigs.
 *  6. FOLIAGE (world config toggle): LEAF PADS in two sizes. At the LAST SPLIT — the first time a
 *     path's set falls to PAD_TIPS (2) or fewer — an oblate ellipsoid is hung on that pair's centroid,
 *     sized to the share of the surface it owns; and a smaller one sits ON TOP of every twig end,
 *     lifted by its own half-thickness. Both are PAD_FLAT as thick as they are wide, both jittered
 *     in size by PAD_SIZE_JITTER, and both have their short axis UP, tilted PAD_TILT of the way toward
 *     the crown's surface normal: level plates on the cap, leaning outward on the flanks, tipped down
 *     under the crotch. A pruned branch has none, so the gaps in the leaves are the gaps in the wood.
 *     (One 1.2 m tuft per tip was 565 noisy spheres that overlapped into a featureless shell; one
 *     large pad per seven tips was ~130 plates that read as too alike.)
 *
 * SIX NUMBERS ARE WORLD CONFIG (`setOakLargeShape`): trunk height, crown radius, canopy range,
 * branch density, pruning, and whether there are leaves. Everything else is derived from them here.
 *
 * THE LADDER (a fine voxel is 0.25 m; a lod-n voxel is 2^n fine):
 *   lod 0   0.25 m    the skeleton (and the tufts), voxel by voxel
 *   lod 1   0.5 m     the LOD-0 model FOLDED — see `foldLod` below
 *   lod 2   1 m       a trunk cylinder + ~18 leaf clumps merged from the tips
 *   lod 3   2 m       a trunk cylinder + ~8 clumps
 *   lod 4   4 m       the crown as one ellipsoid + a trunk pin — a BODY, not a mark
 *   lod 5   8 m       one leaf-coloured column at the tree's own height
 *   lod 6+            nothing: `maxLod` stops at the scatter cutoff — see MAX_LOD below
 */
import { mat } from '../../../materials/index.js';
import { groundSink, pinPart } from '../coarseModel.js';
import { cylinderPart, ellipsoidPart } from '../../../voxel/buildParts.js';
import { MAX_SCATTER_LOD } from '../lodLevels.js';
import { StampType } from '../StampTypes.js';
import { orientedEllipsoid, taperedSegment } from '../../../voxel/blobs.js';
import { minTubeRadiusFine } from '../../../voxel/surfaceBand.js';
import { makeRng } from '../../../util/rng.js';
import { packVoxelKey } from '../../../util/packedKey.js';
/** Length without `Math.hypot`: the varargs form measured 40x a `sqrt` of the sum of squares on V8. */
const len3 = (x, y, z) => Math.sqrt(x * x + y * y + z * z);
/** Fine voxels per metre. */
const M = 4;
const DEG = Math.PI / 180;
/**
 * THE HARD LIMIT ON THE CROWN. The scatter walks MAX_STAMP_REACH_M of neighbours to find the stamps
 * that reach into a chunk; a crown past this radius could be missing from a chunk it reaches —
 * sliced off at a chunk plane. Raise the two together.
 */
export const MAX_CANOPY_R_M = 20;
/**
 * The most any oak reaches from its seat in XZ, metres: the crown radius, a leaf pad on the rim
 * (PAD_R_MAX), the crotch's offset from a 12 m bole at full lean and curve (~2.5 m) and the surface
 * band. StampPointGenerator sizes its neighbour walk from this.
 */
export const MAX_STAMP_REACH_M = MAX_CANOPY_R_M + 8;
export const DEFAULT_OAK_LARGE_SHAPE = {
    trunkM: 4.5, radiusM: 15, rangeDeg: 200, density: 0.3, prune: 0.1, foliage: false,
};
let shape = { ...DEFAULT_OAK_LARGE_SHAPE };
/**
 * Set the shape every large oak is grown to. Returns true when it CHANGED, in which case the caller
 * must invalidate the stamp cache (registry.invalidateStampCache) — the grown skeleton is dropped
 * here, but the placed voxel lists live in the registry and would otherwise stay the old tree.
 */
export function setOakLargeShape(s) {
    const next = {
        trunkM: clamp(s.trunkM ?? shape.trunkM, 2, 12),
        radiusM: clamp(s.radiusM ?? shape.radiusM, 3, MAX_CANOPY_R_M),
        rangeDeg: clamp(s.rangeDeg ?? shape.rangeDeg, 30, 270),
        density: clamp(s.density ?? shape.density, 0.05, 1),
        prune: clamp(s.prune ?? shape.prune, 0, 1),
        foliage: s.foliage ?? shape.foliage,
    };
    if (next.trunkM === shape.trunkM && next.radiusM === shape.radiusM && next.rangeDeg === shape.rangeDeg
        && next.density === shape.density && next.prune === shape.prune && next.foliage === shape.foliage)
        return false;
    shape = next;
    grown = null;
    return true;
}
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
// ============================================================================================
// THE MODEL'S OWN CONSTANTS
// ============================================================================================
/** Radius of the bole, fine voxels — the number every branch radius descends from. */
const TRUNK_R = 1.3 * M;
/** The foot: the flare at the ground, over the first metre. */
const ROOT_FLARE = 1.5;
const ROOT_HEIGHT = 1 * M;
/** The lean: the bole leaves the ground this far off vertical, in a random direction... */
const LEAN_MIN = 3 * DEG;
const LEAN_MAX = 8 * DEG;
/** ...and curves this much further at each of BOLE_STEPS steps on the way up. */
const BOLE_STEPS = 3;
const BOLE_CURVE = 3 * DEG;
/** Limbs at the crotch: the leader plus this many minus one spreaders. */
const CROTCH_LIMBS = 4;
/** The leader's set: every tip within this angle of vertical about the crotch. */
const LEADER_ANGLE = 35 * DEG;
/** How far toward its set's centroid a branch goes before it forks. Higher = longer, fewer segments. */
const LEN_FRAC = 0.35;
/** The most two children may diverge at a fork; a fork is pulled back toward its parent until they fit. */
const MAX_FORK_ANGLE = 60 * DEG;
/** Tips are jittered along the surface by up to this fraction of the lattice spacing. */
const TIP_JITTER = 0.5;
/** Pruning happens where a path's set first falls to this many tips or fewer — the size of a gap. */
const GAP_TIPS = 24;
/** Leonardo's rule: r_child = r_parent × share^(1/EXP). 2 conserves cross-section exactly. */
const LEONARDO_EXP = 3;
/** A segment shorter than this is not drawn: the branch forks where it stands. */
const MIN_SEG = 0.5 * M;
/** Tips lower than this are dropped — a wide range on a short trunk would put wood in the ground. */
const MIN_TIP_Y = 0.5 * M;
/**
 * THE LATTICE FLOOR FOR WOOD — a radius, from surfaceBand, not re-derived. Below it a tube winks in and
 * out; the last few orders of every path sit exactly on it.
 */
const MIN_BRANCH_R = minTubeRadiusFine(1);
/** Foliage: a leaf pad where a path's set first falls to this many tips or fewer (2 = the last split)... */
const PAD_TIPS = 2;
/** ...a smaller pad of this footprint radius on top of every twig end... */
const TIP_PAD_R = 0.8 * M;
/** ...every pad's size jittered by up to this fraction either way... */
const PAD_SIZE_JITTER = 0.25;
/** ...its footprint radius scaled up by this so neighbouring pads overlap into one canopy, and capped
 * (a sparse crown's groups own a lot of surface each; MAX_STAMP_REACH_M counts on the cap)... */
const PAD_OVERLAP = 1.15;
const PAD_R_MAX = 3.5 * M;
/** ...its half-thickness this fraction of its footprint radius... */
const PAD_FLAT = 0.35;
/** ...and its short axis tilted this far (0 up, 1 the surface normal) toward the crown's surface. */
const PAD_TILT = 0.4;
/** Coarse clump counts per lod and the smallest clump a lod may draw (× stride). */
const COARSE_CLUMPS = { 2: 18, 3: 8 };
const COARSE_CLUMP_MIN_R = 1.05;
/** The one shape every variant grows: v0. */
const SHAPE_SEED = 0x0a4b;
function params() {
    const trunkH = shape.trunkM * M;
    const R = shape.radiusM * M;
    return {
        trunkH, R,
        thetaMax: shape.rangeDeg * 0.5 * DEG,
        density: shape.density,
        prune: shape.prune,
        foliage: shape.foliage,
        height: trunkH + R,
        trunkR: TRUNK_R,
        barkMat: mat('bark'),
        // A canopy this wide repeats its texture far more times than the small oak's does, and the shared
        // leaf tiles visibly at that count. This one is authored to tile.
        leafMat: mat('leaves_oak_trans_tileable'),
    };
}
/** Grow the tree: the bole, the tiled surface, then the fork rule until every tip is reached. */
function grow(p, rand) {
    const wood = [];
    const pads = [];
    // ------------------------------------------------------------------------------------------
    // THE BOLE, started BELOW THE SEAT: `taperedSegment` caps its ends with hemispheres, so a bole
    // starting at y = 0 would round its own foot off in the open air above the ground it stands on.
    // The extra is buried and never drawn.
    // ------------------------------------------------------------------------------------------
    const leanAz = rand() * Math.PI * 2;
    let lean = LEAN_MIN + rand() * (LEAN_MAX - LEAN_MIN);
    const ax = Math.cos(leanAz), az = Math.sin(leanAz);
    let bx = ax * Math.tan(lean) * ROOT_HEIGHT, by = ROOT_HEIGHT, bz = az * Math.tan(lean) * ROOT_HEIGHT;
    wood.push({ x0: 0, y0: -ROOT_HEIGHT, z0: 0, x1: bx, y1: by, z1: bz, r0: TRUNK_R * ROOT_FLARE, r1: TRUNK_R });
    const rise = (p.trunkH - ROOT_HEIGHT) / BOLE_STEPS;
    for (let i = 0; i < BOLE_STEPS; i++) {
        lean += BOLE_CURVE;
        const run = rise * Math.tan(lean);
        const nx = bx + ax * run, ny = by + rise, nz = bz + az * run;
        wood.push({ x0: bx, y0: by, z0: bz, x1: nx, y1: ny, z1: nz, r0: TRUNK_R, r1: TRUNK_R });
        bx = nx;
        by = ny;
        bz = nz;
    }
    const cx = bx, cy = by, cz = bz;
    // ------------------------------------------------------------------------------------------
    // THE TIPS: a Fibonacci lattice over the spherical cap, `density` per m² of its area.
    // ------------------------------------------------------------------------------------------
    const cosMax = Math.cos(p.thetaMax);
    const areaM2 = 2 * Math.PI * (p.R / M) * (p.R / M) * (1 - cosMax);
    const N = Math.max(CROTCH_LIMBS, Math.round(areaM2 * p.density));
    const tx = new Float64Array(N), ty = new Float64Array(N), tz = new Float64Array(N);
    const GOLDEN = Math.PI * (3 - Math.sqrt(5));
    // The lattice spacing in fine voxels; the jitter is a random vector up to TIP_JITTER of it, added
    // to the unit direction and renormalised — a nudge along the surface.
    const jitter = TIP_JITTER * (M / Math.sqrt(p.density)) / p.R;
    let n = 0;
    for (let i = 0; i < N; i++) {
        const c = 1 - ((i + 0.5) / N) * (1 - cosMax);
        const s = Math.sqrt(Math.max(0, 1 - c * c));
        const phi = i * GOLDEN;
        let ux = s * Math.cos(phi) + (rand() * 2 - 1) * jitter;
        let uy = c + (rand() * 2 - 1) * jitter;
        let uz = s * Math.sin(phi) + (rand() * 2 - 1) * jitter;
        const ul = len3(ux, uy, uz) || 1;
        ux /= ul;
        uy /= ul;
        uz /= ul;
        const y = cy + p.R * uy;
        if (y < MIN_TIP_Y)
            continue;
        tx[n] = cx + p.R * ux;
        ty[n] = y;
        tz[n] = cz + p.R * uz;
        n++;
    }
    const total = n;
    /** Surface area per tip, fine voxels² — what a pad's footprint is sized from. */
    const areaPerTip = (areaM2 * M * M) / total;
    const idx = new Int32Array(total);
    for (let i = 0; i < total; i++)
        idx[i] = i;
    const radiusFor = (count) => Math.max(MIN_BRANCH_R, TRUNK_R * Math.pow(count / total, 1 / LEONARDO_EXP));
    /**
     * Split idx[lo, hi) about its farthest pair, in place: the tips nearer the first end up in
     * [lo, mid), the rest in [mid, hi). Returns mid.
     */
    function split(lo, hi) {
        let a = lo, b = lo + 1, best = -1;
        for (let i = lo; i < hi; i++) {
            const ti = idx[i];
            for (let j = i + 1; j < hi; j++) {
                const tj = idx[j];
                const d = (tx[ti] - tx[tj]) ** 2 + (ty[ti] - ty[tj]) ** 2 + (tz[ti] - tz[tj]) ** 2;
                if (d > best) {
                    best = d;
                    a = i;
                    b = j;
                }
            }
        }
        const ta = idx[a], tb = idx[b];
        let mid = lo;
        for (let i = lo; i < hi; i++) {
            const t = idx[i];
            const da = (tx[t] - tx[ta]) ** 2 + (ty[t] - ty[ta]) ** 2 + (tz[t] - tz[ta]) ** 2;
            const db = (tx[t] - tx[tb]) ** 2 + (ty[t] - ty[tb]) ** 2 + (tz[t] - tz[tb]) ** 2;
            if (da <= db) {
                idx[i] = idx[mid];
                idx[mid] = t;
                mid++;
            }
        }
        return mid;
    }
    /** Centroid of idx[lo, hi) into `out`. */
    const cen = { x: 0, y: 0, z: 0 };
    function centroid(lo, hi, out) {
        let mx = 0, my = 0, mz = 0;
        for (let i = lo; i < hi; i++) {
            const t = idx[i];
            mx += tx[t];
            my += ty[t];
            mz += tz[t];
        }
        const inv = 1 / (hi - lo);
        out.x = mx * inv;
        out.y = my * inv;
        out.z = mz * inv;
    }
    const cenA = { x: 0, y: 0, z: 0 }, cenB = { x: 0, y: 0, z: 0 };
    /** The angle at (x, y, z) between the directions to the two centroids. */
    function forkAngle(x, y, z) {
        const ax = cenA.x - x, ay = cenA.y - y, az = cenA.z - z;
        const bx = cenB.x - x, by = cenB.y - y, bz = cenB.z - z;
        const d = (ax * bx + ay * by + az * bz) / ((len3(ax, ay, az) * len3(bx, by, bz)) || 1);
        return Math.acos(Math.max(-1, Math.min(1, d)));
    }
    /**
     * A leaf pad at (x, y, z) of footprint `r0` (jittered), its short axis up tilted toward the surface
     * normal there, lifted `lift` half-thicknesses along that axis (1 = sitting on top of the point).
     */
    function pad(x, y, z, r0, lift) {
        let nx = x - cx, ny = y - cy, nz = z - cz;
        const nl = len3(nx, ny, nz) || 1;
        nx = (nx / nl) * PAD_TILT;
        ny = (ny / nl) * PAD_TILT + (1 - PAD_TILT);
        nz = (nz / nl) * PAD_TILT;
        const al = len3(nx, ny, nz) || 1;
        nx /= al;
        ny /= al;
        nz /= al;
        const r = r0 * (1 + (rand() * 2 - 1) * PAD_SIZE_JITTER);
        const h = r * PAD_FLAT;
        pads.push({ x: x + nx * h * lift, y: y + ny * h * lift, z: z + nz * h * lift, r, h, nx, ny, nz });
    }
    /**
     * THE FORK RULE — see the header. A branch of radius `r` at (x, y, z) owning idx[lo, hi), leaving
     * its parent at radius `collar` (the parent's own, at the crotch) and tapering to `r` over its
     * first segment. `parentCount` is the size of the set this one was cut from: pruning is decided
     * the first time a path's set is GAP_TIPS or fewer, and nowhere else.
     */
    function branch(x, y, z, lo, hi, r, collar, parentCount) {
        const count = hi - lo;
        if (count <= GAP_TIPS && parentCount > GAP_TIPS && rand() < p.prune)
            return; // PRUNED — see header
        if (count <= PAD_TIPS && parentCount > PAD_TIPS) {
            // THE LAST SPLIT'S PAD, on the group's centroid, sized to the surface it owns — see the header.
            centroid(lo, hi, cen);
            pad(cen.x, cen.y, cen.z, Math.min(PAD_R_MAX, Math.sqrt((count * areaPerTip) / Math.PI) * PAD_OVERLAP), 0);
        }
        if (count === 1) {
            const t = idx[lo];
            wood.push({ x0: x, y0: y, z0: z, x1: tx[t], y1: ty[t], z1: tz[t], r0: collar, r1: r });
            pad(tx[t], ty[t], tz[t], TIP_PAD_R, 1); // ...and the small one ON TOP of the end
            return;
        }
        centroid(lo, hi, cen);
        const mx = cen.x - x, my = cen.y - y, mz = cen.z - z;
        const dl = len3(mx, my, mz) || 1;
        const mid = split(lo, hi);
        centroid(lo, mid, cenA);
        centroid(mid, hi, cenB);
        // The fork point: LEN_FRAC of the way to the centroid, pulled back by halves while the children
        // would diverge by more than MAX_FORK_ANGLE there.
        let len = dl * LEN_FRAC;
        let ex = x + (mx / dl) * len, ey = y + (my / dl) * len, ez = z + (mz / dl) * len;
        while (len >= MIN_SEG && forkAngle(ex, ey, ez) > MAX_FORK_ANGLE) {
            len *= 0.5;
            ex = x + (mx / dl) * len;
            ey = y + (my / dl) * len;
            ez = z + (mz / dl) * len;
        }
        // No segment drawn: the collar is handed on so the taper still happens on the next length.
        let drawn = true;
        if (len < MIN_SEG) {
            ex = x;
            ey = y;
            ez = z;
            drawn = false;
        }
        else
            wood.push({ x0: x, y0: y, z0: z, x1: ex, y1: ey, z1: ez, r0: collar, r1: r });
        const rA = radiusFor(mid - lo), rB = radiusFor(hi - mid);
        branch(ex, ey, ez, lo, mid, rA, drawn ? rA : collar, count);
        branch(ex, ey, ez, mid, hi, rB, drawn ? rB : collar, count);
    }
    // THE CROTCH: the LEADER takes the cap — every tip within LEADER_ANGLE of vertical, gathered to the
    // front of idx — and the rest is cut into spreaders, the largest group split each time. A range so
    // narrow that the cap is everything (or so wide-and-low that it is nothing) falls back to splitting.
    const cosLeader = Math.cos(LEADER_ANGLE);
    let k = 0;
    for (let i = 0; i < total; i++) {
        const t = idx[i];
        if ((ty[t] - cy) / p.R >= cosLeader) {
            idx[i] = idx[k];
            idx[k] = t;
            k++;
        }
    }
    const groups = k > 0 && k < total ? [[0, k], [k, total]] : [[0, total]];
    while (groups.length < CROTCH_LIMBS) {
        // Split the largest SPREADER group; the leader (group 0 when it exists) is never cut.
        const first = groups.length > 1 && k > 0 && k < total ? 1 : 0;
        let gi = first;
        for (let i = first + 1; i < groups.length; i++)
            if (groups[i][1] - groups[i][0] > groups[gi][1] - groups[gi][0])
                gi = i;
        const [lo, hi] = groups[gi];
        if (hi - lo < 2)
            break;
        const mid = split(lo, hi);
        groups.splice(gi, 1, [lo, mid], [mid, hi]);
    }
    // The crotch limbs leave at the bole's radius; `total` as the parent count so none is pruned whole.
    for (const [lo, hi] of groups)
        branch(cx, cy, cz, lo, hi, radiusFor(hi - lo), TRUNK_R, total);
    return { wood, pads, voxels: null, clumps: new Map() };
}
/**
 * The skeleton, grown once per shape. Reset by `setOakLargeShape`; both LOD 0 and the coarse clumps
 * read it, and growing it twice would be the same answer at twice the cost.
 */
let grown = null;
function skeleton(p) {
    if (!grown)
        grown = grow(p, makeRng(SHAPE_SEED));
    return grown;
}
/**
 * LOD 0 — every tuft (if foliage) and every capsule of wood, then DEDUPED: one entry per position,
 * the LAST write kept, which is exactly the rule StampPlacer applies, so a deduped list places
 * identically and costs less to store and to iterate per chunk. Foliage goes first so wood wins where
 * they overlap and is seen through the transparent leaves. A band (negative-weight) sample never
 * overwrites a solid one: the placer's own rule is a max for the band.
 *
 * ONE LIST FOR EVERY VARIANT — the spec declares a single variant (see `variants`), because the four
 * copies the registry would otherwise hold of a 15 m crown in leaf would be most of its cache.
 */
function lod0(p) {
    const sk = skeleton(p);
    if (sk.voxels)
        return sk.voxels;
    const raw = [];
    if (p.foliage) {
        for (const d of sk.pads)
            raw.push(...orientedEllipsoid(d.x, d.y, d.z, d.nx, d.ny, d.nz, d.r, d.h, p.leafMat));
    }
    for (const s of sk.wood) {
        raw.push(...taperedSegment(s.x0, s.y0, s.z0, s.x1, s.y1, s.z1, s.r0, s.r1, p.barkMat));
    }
    const slot = new Map();
    const out = [];
    for (const v of raw) {
        const key = packVoxelKey(v.x, v.y, v.z);
        const at = slot.get(key);
        if (at === undefined) {
            slot.set(key, out.length);
            out.push(v);
            continue;
        }
        const held = out[at];
        if (v.weight < 0) {
            if (held.weight < 0 && v.weight > held.weight)
                out[at] = v;
            continue;
        }
        out[at] = v;
    }
    sk.voxels = out;
    return out;
}
/**
 * FOLDED AT LOD 1, WHICH THE SMALL TREES MUST NOT BE — a fact about the model rather than a
 * preference. A fold keeps whatever survives the coarse lattice (voxel/fold.ts). The small oak's
 * trunk is 1.3 fine voxels of RADIUS, thinner than a lod-1 voxel, so folding it leaves a canopy in
 * the air. This bole is 5.2 voxels of radius and its crotch limbs ~3, so at 0.5 m per voxel the bole
 * is ten cells wide and the limbs six: the branch structure transfers, and a stand-in at lod 1 would
 * throw away the very thing this tree exists for. The twigs, at the floor, dot.
 */
const FOLD_LOD = 1;
/**
 * IT STOPS AT THE SCATTER CUTOFF, like a building and unlike the small trees. Above MAX_SCATTER_LOD
 * the far rings paint one dot per column, sized by `registry.columnDot()` over every spec still
 * drawn up there — honest for a saturated scatter (an oak per 5 m), not for one tree per 40 m and
 * 20 m tall. Capping here is the statement "no picture of me above the cutoff".
 */
const MAX_LOD = MAX_SCATTER_LOD;
/**
 * The leaf pads merged down to `target` clumps for a lod, once, memoised on the skeleton.
 *
 * TWO STAGES, because a pairwise merge alone is O(n³). First the pads are BINNED into cells four
 * coarse voxels wide and merged within each cell (O(n)); only the few dozen survivors go through the
 * nearest-pair merge down to the target. A merge conserves leaf VOLUME (r³ adds) and then makes sure
 * the result still SPANS the pair it replaced.
 */
function coarseClumps(p, lod) {
    const sk = skeleton(p);
    const held = sk.clumps.get(lod);
    if (held)
        return held;
    const target = COARSE_CLUMPS[lod];
    const cell = 4 << lod;
    const bins = new Map();
    for (const c of sk.pads) {
        const key = packVoxelKey(Math.floor(c.x / cell), Math.floor(c.y / cell), Math.floor(c.z / cell));
        const b = bins.get(key);
        if (!b) {
            bins.set(key, { ...c });
            continue;
        }
        bins.set(key, mergePair(b, c));
    }
    const out = [...bins.values()];
    while (out.length > target) {
        let bi = 0, bj = 1, bd = Infinity;
        for (let i = 0; i < out.length; i++) {
            for (let j = i + 1; j < out.length; j++) {
                const d = len3(out[i].x - out[j].x, out[i].y - out[j].y, out[i].z - out[j].z);
                if (d < bd) {
                    bd = d;
                    bi = i;
                    bj = j;
                }
            }
        }
        out[bi] = mergePair(out[bi], out[bj]);
        out.splice(bj, 1);
    }
    sk.clumps.set(lod, out);
    return out;
}
function mergePair(a, b) {
    const d = len3(a.x - b.x, a.y - b.y, a.z - b.z);
    const wa = a.r ** 3, wb = b.r ** 3, w = wa + wb;
    const r = Math.max(Math.cbrt(w), (d + a.r + b.r) * 0.5 * 0.85);
    return {
        x: (a.x * wa + b.x * wb) / w, y: (a.y * wa + b.y * wb) / w, z: (a.z * wa + b.z * wb) / w,
        r, h: r * PAD_FLAT, nx: 0, ny: 1, nz: 0,
    };
}
function lodParts(p, lod) {
    const stride = 1 << lod;
    // Where the COARSE trunk ends — up inside the crown, not at the crotch (see treeOak.trunkTop).
    const trunkTop = p.trunkH + p.R * 0.3;
    if (lod <= 3) {
        // The bole, run up PAST the crotch into the crown so wood reads through the transparent leaves.
        // Floored at just over a coarse voxel: a sub-voxel cylinder draws as a bare axis.
        const trunk = cylinderPart(Math.max(p.trunkR, 1.05 * stride), trunkTop + groundSink(stride), p.barkMat, -groundSink(stride));
        // THE CROWN AS CLUMPS, NOT A DOME: the leaf pads merged down, so the crown keeps its plates, fewer
        // and bigger — axis-aligned here (a tilt is nothing at 1-2 m voxels), each held to at least a
        // coarse voxel or it draws as a spike.
        const parts = [trunk];
        for (const c of coarseClumps(p, lod)) {
            const r = Math.max(c.r, COARSE_CLUMP_MIN_R * stride);
            const h = Math.max(c.h, COARSE_CLUMP_MIN_R * stride);
            const y = Math.min(c.y, p.height - h);
            parts.push({ ...ellipsoidPart(r, h, y, p.leafMat), offset: { x: c.x, y, z: c.z } });
        }
        return parts;
    }
    if (lod === 4) {
        // STILL A BODY AT 4 m — the crown as one ellipsoid over the cap's extent. A crown 30 m across is
        // 7 voxels at this stride; a pin here throws all of that away one ring too early.
        const rx = p.R * (p.thetaMax >= Math.PI / 2 ? 1 : Math.sin(p.thetaMax));
        const yLo = Math.max(MIN_TIP_Y, p.trunkH + p.R * Math.cos(p.thetaMax));
        const crown = ellipsoidPart(rx, (p.height - yLo) / 2, (p.height + yLo) / 2, p.leafMat);
        return [pinPart(trunkTop, p.barkMat, stride), crown];
    }
    // lod 5: narrower than a voxel in every direction — a column of the right HEIGHT.
    return [pinPart(p.height, p.leafMat, stride)];
}
export const TREE_OAK_LARGE_SPEC = {
    type: StampType.TREE_OAK_LARGE,
    maxLod: MAX_LOD,
    foldLod: FOLD_LOD,
    variants: 1,
    params,
    lod0,
    lodParts,
};
//# sourceMappingURL=treeOakLarge.js.map